import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { takeUntil, Subject } from 'rxjs';

import { AppButtonModule } from 'src/app/components/button/button.module';
import { ExceptionName } from 'src/app/models/common';
import { UserService } from 'src/app/services/user.service';
import { ToasterService } from 'src/app/utils/toaster.service';
import { ConfirmPasswordValidator } from 'src/app/validators/confirm-password.validator';
import { NewPasswordNotSameAsCurrentValidator } from 'src/app/validators/new-password-not-same-as-current.validator';

/**
 * Shared "Change password" dialog, opened via `BsModalService.show(ChangePasswordModalComponent, ...)`
 * from both the Profile page and the header profile dropdown so the two entry points behave identically.
 */
@Component({
  selector: 'app-change-password-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AppButtonModule],
  templateUrl: './change-password-modal.component.html',
  styleUrls: ['./change-password-modal.component.scss'],
})
export class ChangePasswordModalComponent implements OnInit {
  public form!: FormGroup;
  public isSubmitting = false;
  public currentPasswordVisible = false;
  public newPasswordVisible = false;
  public confirmNewPasswordVisible = false;

  private readonly ngUnSubscribe = new Subject<void>();

  constructor(
    public modalRef: BsModalRef,
    private fb: FormBuilder,
    private userService: UserService,
    private toaster: ToasterService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group(
      {
        currentPassword: ['', [Validators.required]],
        newPassword: [
          '',
          [Validators.required, Validators.minLength(6), Validators.maxLength(30)],
        ],
        confirmNewPassword: [
          '',
          [Validators.required, Validators.minLength(6), Validators.maxLength(30)],
        ],
      },
      {
        validators: [
          ConfirmPasswordValidator('newPassword', 'confirmNewPassword'),
          NewPasswordNotSameAsCurrentValidator('currentPassword', 'newPassword'),
        ],
      }
    );

    this.form
      .get('currentPassword')
      ?.valueChanges.pipe(takeUntil(this.ngUnSubscribe))
      .subscribe(() => {
        this.form.get('newPassword')?.updateValueAndValidity({ emitEvent: false });
        this.form.updateValueAndValidity({ emitEvent: false });
        this.cd.markForCheck();
      });

    this.form
      .get('newPassword')
      ?.valueChanges.pipe(takeUntil(this.ngUnSubscribe))
      .subscribe(() => {
        this.form.updateValueAndValidity({ emitEvent: false });
        this.cd.markForCheck();
      });

    this.form
      .get('confirmNewPassword')
      ?.valueChanges.pipe(takeUntil(this.ngUnSubscribe))
      .subscribe(() => {
        this.form.updateValueAndValidity({ emitEvent: false });
        this.cd.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.ngUnSubscribe.next();
    this.ngUnSubscribe.complete();
  }

  close(): void {
    this.modalRef.hide();
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  showFieldError(controlName: string, errorKey?: string): boolean {
    const control = this.form.get(controlName);
    if (!control || !(control.dirty || control.touched)) {
      return false;
    }
    if (!errorKey) {
      return control.invalid;
    }
    return !!control.errors?.[errorKey];
  }

  submit(): void {
    if (!this.form.valid || this.isSubmitting) {
      this.form.markAllAsTouched();
      this.cd.markForCheck();
      return;
    }

    this.isSubmitting = true;
    this.cd.markForCheck();

    const payload = {
      currentPassword: this.form.get('currentPassword')?.value,
      newPassword: this.form.get('newPassword')?.value,
      updatePasswordType: 'Change',
    };

    this.userService
      .changePasswordAsync(payload)
      .pipe(takeUntil(this.ngUnSubscribe))
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.toaster.showSuccess('Password updated successfully.');
          this.close();
        },
        error: (error) => {
          this.isSubmitting = false;
          if (error?.error?.title === ExceptionName.currentPasswordMismatch) {
            this.toaster.showError(
              'The current password you entered is incorrect. Please try again.'
            );
          }
          this.cd.markForCheck();
        },
      });
  }
}
